import axios from "axios";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { FINDINGS, IMPORT_GENPT_TESTS, SIEMBAGPT_SESSIONS, SIEMBAGPT_FINDINGS, SIEMBAGPT_SESSION_DETAILS, SIEMBAGPT_SESSION_INSIGHTS, SIEMBAGPT_USER_SESSION_INSIGHTS, GENPT_TEST_KEY_INSIGHT, CVE_IDs, SAVE_CVE_IDs } from "./utils/constants";
import { paginationAttr } from "../actions/commonActions";

export const useGenPTSessions = (options = {}) => {
  const {
    search = null,
    pageSize = 10,
    sortBy = [],
    enabled = true,
    userId = null,
    tags,
    status, 
    startDate, 
    endDate,
    getInsights,
    batchId,
    notificationStartDateTime,
    notificationEndDateTime,
    getUnique,
    ...queryOptions
  } = options
  return useInfiniteQuery([SIEMBAGPT_SESSIONS, {
    search, 
    pageSize, 
    sortBy, 
    userId,
    tags,
    status, 
    startDate, 
    endDate,
    getInsights,
    batchId,
    notificationStartDateTime,
    notificationEndDateTime,
    getUnique
  }], async ({ pageParam = 1 }) => {
      let res = await axios.get(`/genpt/user/sessions${paginationAttr({ 
        page: pageParam, 
        pageSize, 
        search, 
        sortBy,  
        tags,
        status, 
        startDate, 
        endDate,
        getInsights,
        batchId,
        notificationStartDateTime,
        notificationEndDateTime,
        getUnique
      })}`);
      return res.data
  },{
    getNextPageParam: lastPage => lastPage?.nextPage ?? undefined,
    enabled,
    keepPreviousData: true,
    ...queryOptions
  })
};

export const useDeleteGenPTSession = () => {
  const queryClient = useQueryClient();
  return useMutation(async (sessionId) => {
    let res = await axios.delete(`/genpt/sessions/${sessionId}`)
    return res.data
}, {
  onSuccess: async () => {
      await queryClient.invalidateQueries(SIEMBAGPT_SESSIONS);
      await queryClient.invalidateQueries(SIEMBAGPT_USER_SESSION_INSIGHTS);
  }
});
}

export const useGenPTSessionDetails = (options = {}) => {
  const {
    sessionId = '',
    checkEnriched = false,
    ...queryOptions
} = options
  return useQuery([SIEMBAGPT_SESSION_DETAILS, sessionId, checkEnriched], async () => {
    let res = await axios.get(`/genpt/sessions/${sessionId}${checkEnriched ? '?checkEnriched=true' : ''}`);
    return res.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const useGenPTSessionInsights = (options = {}) => {
  const {
    sessionId = '',
    ...queryOptions
} = options
  return useQuery([SIEMBAGPT_SESSION_INSIGHTS, sessionId], async () => {
    let res = await axios.get(`/genpt/sessions/${sessionId}/insights`);
    return res.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const useGenptKeyInsightsDetailsById = (options={}) => {
  const {
    sortBy = [],
    sessionId = null,
    insightId = null,
  } = options
  return useQuery([GENPT_TEST_KEY_INSIGHT, {
    sortBy,
    sessionId,
    insightId,
  }], async () => {
    let attr = "";
    let sortValue = "";
    sortBy?.forEach((item, index) => {
        let sortKey = item.id?.replaceAll('_', '');
        let dir = item.desc ? "desc" : "asc";
        sortValue += `${index > 0 ? "," : ""}${sortKey}_${dir}`;
    });

    if (sortValue) attr += `?sort=${sortValue}`;

    let res = await axios.get(`/genpt/sessions/${sessionId}/insights/${insightId}${attr}`);
    return res?.data;
  }, {
      enabled: !!sessionId && !!insightId,
      keepPreviousData: true
  })
}

export const useFetchGenPTConfigurations = (options = {}) => {
  const {
    ...queryOptions
} = options
  return useQuery(['GENPT_CONFIGURATIONS'], async () => {
    let res = await axios.get(`/genpt/configurations`);
    return res.data
  }, {
    ...queryOptions
  })
};

export const useGenPTUserTestsInsights = (options = {}) => {
  const {
    userId = '',
    ...queryOptions
} = options
  return useQuery([SIEMBAGPT_USER_SESSION_INSIGHTS, userId], async () => {
    let res = await axios.get(`/genpt/user/tests/insights`);
    return res.data
  }, {
    enabled: !!userId,
    ...queryOptions
  })
};

export const useFetchSessionResponses = (options = {}) => {
  const {
    sessionId = '',
    ...queryOptions
} = options
  return useQuery(['Session Responses', sessionId], async () => {
    const res = await axios.get(`/genpt/sessions/${sessionId}/latestResponse`);
    return res.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const useFetchLatestSessionActivity = (options = {}) => {
  const {
    sessionId = '',
    activityId = '',
    ...queryOptions
} = options
  return useQuery(['Session Activities', sessionId, activityId], async () => {
    const response = await axios.get(`/genpt/sessions/${sessionId}/activity?activityId=${activityId}`);
    return response.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const fetchActivityScreenshots = async (sessionId, activityId) => {
  try {
    const response = await axios.get(`/genpt/sessions/${sessionId}/activity?activityId=${activityId}`);
    return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
}

export const useStartGenPTEngine = (options) => {
  return useMutation(async (data) => {
      // Extract nonce from metadata?.nonce if available
      const nonce = data?.metadata?.nonce || data?.nonce || null;
      const chat_id = data?.chat_id || null;
      
      // Prepare payload with nonce and chat_id
      const payload = {
        ...data,
        nonce,
        chat_id,
      };
      
      let res = await axios.post(`/genpt/test`, payload)
      return res.data
  }, options)
};

export const useFetchAssetCredentials = (assetId, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  return useQuery(
    ['assetCredentials', assetId],
    async () => {
      if (!assetId) return null;
      const res = await axios.get(`/assets/credentials`, {
        params: { assetId },
      });
      return res.data;
    },
    {
      enabled: enabled && !!assetId,
      ...queryOptions,
    }
  );
};

export const usePutExternalTicket = (findingId) => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.put(`/genpt/findings/external_ticket/${findingId}`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      await queryClient.invalidateQueries(FINDINGS);
    }
  })
};

export const useValidateTarget = (options) => {
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/validate_target`, data)
      return res.data
  }, options)
};

export const useValidateAPICollection = (options) => {
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/validate_api_collection`, data)
      return res.data
  }, options)
};

export const useGenPTTestSteps = (options) => {
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/test/steps`, data)
      return res.data
  }, options)
};

export const useEnrichFindings = (sessionId, findingId, options) => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/sessions/${sessionId}/findings/${findingId}/enrichment`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      await queryClient.invalidateQueries(FINDINGS);
    },
    ...options
  })
};

export const useSaveSessionProgress = (sessionId) => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.put(`/genpt/sessions/${sessionId}/save`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      // await queryClient.invalidateQueries(FINDINGS);
    }
  })
};

export const getSessionDetails = async (sessionId) => {
  try {
  const response = await axios.get(`/genpt/sessions/${sessionId}`);
  return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
};

export const fetchSessionFindings = async (sessionId) => {
  try{
    let res = await axios.get(`/genpt/sessions/${sessionId}/findings`);
    return res.data
  }catch (err){
    console.error('Error scanning:', err);
  }
};

export const useFetchAllFindings = (options = {}) => {
  const {
    search = null,
    pageSize = 10,
    sortBy = [],
    enabled = true,
    status = null,
    severity = null,
    sessionId = null,
    cve = [],
    startDate = "",
    endDate = "",
  } = options
  return useInfiniteQuery([SIEMBAGPT_FINDINGS, {
    search,
    pageSize,
    sortBy,
    status,
    severity,
    sessionId,
    cve,
    startDate,
    endDate,
}], async ({ pageParam = 1 }) => {
      let res = await axios.get(`/genpt/findings${paginationAttr({ page: pageParam, pageSize, search, sortBy, status, severity, sessionId, cve, startDate, endDate })}`);
      return res?.data
  },{
    getNextPageParam: lastPage => lastPage?.nextPage ?? undefined,
    enabled,
    keepPreviousData: true
  })
}

export const fetchSessionFindingDetails = async (sessionId,findingId) => {
  try{
    let res = await axios.get(`/genpt/sessions/${sessionId}/findings/${findingId}`);
    return res.data
  }catch (err){
    console.error('Error scanning:', err);
  }
};

export const getSessionResponses = async (sessionId) => {
  try {
  const response = await axios.get(`/genpt/sessions/${sessionId}/latestResponse`);
  return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
};

export const useImportGenptTests = (options) => {
  const queryClient = useQueryClient();
  return useMutation(
      async (data) => {

          let res = await axios.post(`/genpt/sessions/import`, data, {
              headers: {
                  'Content-Type': 'multipart/form-data', // Set the content type as multipart/form-data
              },
          });
          return res.data;
      },
      options,
      {
          onSuccess: async () => {
              await queryClient.invalidateQueries(IMPORT_GENPT_TESTS);
          },
      }
  );
};

export const useStartBulkTest = () => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/bulk_test`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      await queryClient.invalidateQueries(SIEMBAGPT_SESSIONS);
      await queryClient.invalidateQueries(SIEMBAGPT_USER_SESSION_INSIGHTS);
    }
  })
};

export const useCVEIDs = (options = {}) => {
  return useQuery(
    CVE_IDs,
    async () => {
      let res = await axios.get(`/genpt/cve_ids`);
      return res?.data?.data;
    },
    {
      ...options,
    }
  );
};

export const useSaveCVEIDs = (options = {}) => {
  return useQuery(
    SAVE_CVE_IDs,
    async () => {
      let res = await axios.get(`/genpt/save_cve_ids`);
      return res?.data?.data;
    },
    {
      ...options,
    }
  );
};

export const refreshGenPTOverviewCache = async () => {
  try {
  const response = await axios.post(`/genpt/refresh_genpt_overview`);
  return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
};
